import { logger } from "@/lib/logger"
import { createBrowserClient as createSupabaseBrowserClient } from "@supabase/ssr"
import type { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js"
import { createSupabaseStorageConfig } from "@/lib/supabase/client-storage"

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

// Validate the URL format
if (!supabaseUrl || !supabaseUrl.startsWith("https://")) {
  logger.error("❌ Invalid Supabase URL! Expected format: https://your-project.supabase.co")
}

if (!supabaseAnonKey || (!supabaseAnonKey.startsWith("sb_publishable_") && supabaseAnonKey.length < 100)) {
  logger.error("❌ Invalid Supabase Anon Key! Expected a publishable key (sb_publishable_…) or a legacy anon JWT")
}

let cachedClient: SupabaseClient | null = null
let isChannelPatched = false

/**
 * Patch RealtimeChannel.subscribe to prevent "tried to subscribe multiple times" error
 * This happens in React 18 Strict Mode when components mount/unmount/remount rapidly
 */
function patchChannelSubscribe(client: SupabaseClient) {
  if (isChannelPatched || typeof window === 'undefined') return

  try {
    // Get a test channel to access the prototype
    const testChannel = client.channel('__patch_test__')
    const proto = Object.getPrototypeOf(testChannel)

    if (proto && proto.subscribe && !proto._originalSubscribe) {
      proto._originalSubscribe = proto.subscribe
      proto.subscribe = function(this: RealtimeChannel, callback?: (status: string, err?: Error) => void) {
        const state = (this as any).state

        // If already subscribed or subscribing, don't call subscribe again
        if (state === 'joined' || state === 'joining') {
          logger.debug(`[RealtimeChannel] Skipping duplicate subscribe for channel in state '${state}'`)
          // Still call the callback with current status if provided
          if (callback) {
            setTimeout(() => {
              callback(state === 'joined' ? 'SUBSCRIBED' : 'CHANNEL_ERROR')
            }, 0)
          }
          return this
        }

        // Call original subscribe with error handling
        try {
          return proto._originalSubscribe.call(this, callback)
        } catch (err: any) {
          if (err?.message?.includes('tried to subscribe multiple times')) {
            logger.debug('[RealtimeChannel] Caught duplicate subscribe error, ignoring')
            if (callback) {
              setTimeout(() => callback('SUBSCRIBED'), 0)
            }
            return this
          }
          throw err
        }
      }
      isChannelPatched = true
      logger.debug('[Supabase] RealtimeChannel.subscribe patched for React 18 compatibility')
    }

    // Clean up test channel
    client.removeChannel(testChannel)
  } catch (err) {
    logger.warn('[Supabase] Failed to patch RealtimeChannel:', err)
  }
}

/**
 * createClient
 * Returns the singleton Supabase client.
 * If the client does not yet exist, it will be created with the
 * correct URL & anon key from environment variables.
 */
export function createClient(): SupabaseClient {
  if (!cachedClient) {
    try {
      const storageConfig = createSupabaseStorageConfig()
      cachedClient = createSupabaseBrowserClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          ...storageConfig,
          detectSessionInUrl: true,
          persistSession: true,
          autoRefreshToken: true
        }
      })

      // Patch the channel subscribe method to handle React 18 Strict Mode
      patchChannelSubscribe(cachedClient)
    } catch (error) {
      logger.error("❌ Failed to create Supabase client:", error)
      throw error
    }
  }
  return cachedClient
}

/**
 * supabase
 * A ready-to-use singleton instance for convenience.
 * Lazily initialized to avoid TDZ errors during module loading.
 */
let _supabaseInstance: SupabaseClient | null = null
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(target, prop) {
    if (!_supabaseInstance) {
      _supabaseInstance = createClient()
    }
    return (_supabaseInstance as any)[prop]
  }
})

/**
 * Connection health check utilities
 */
export const connectionHealth = {
  /**
   * Check if the Supabase connection is healthy
   */
  async checkConnection(): Promise<boolean> {
    try {
      const { error } = await supabase.auth.getSession()
      return !error
    } catch {
      return false
    }
  },

  /**
   * Check if real-time is available
   */
  async checkRealtime(): Promise<boolean> {
    try {
      const testChannel = supabase.channel('health-check-' + Date.now())
      
      return new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          supabase.removeChannel(testChannel)
          resolve(false)
        }, 5000) // 5 second timeout
        
        testChannel
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              clearTimeout(timeout)
              supabase.removeChannel(testChannel)
              resolve(true)
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              clearTimeout(timeout)
              supabase.removeChannel(testChannel)
              resolve(false)
            }
          })
      })
    } catch {
      return false
    }
  },

  /**
   * Get connection diagnostics
   */
  async getDiagnostics() {
    const [hasConnection, hasRealtime, session] = await Promise.all([
      this.checkConnection(),
      this.checkRealtime(),
      supabase.auth.getSession()
    ])

    return {
      connected: hasConnection,
      realtime: hasRealtime,
      authenticated: !!session.data.session,
      sessionExpiry: session.data.session?.expires_at,
      timestamp: new Date().toISOString()
    }
  }
}

// Export createBrowserClient for compatibility with components expecting it
export { createClient as createBrowserClient }

export default supabase
